import { DateTimeFormatPipe } from '@/extensions/dateTimePipe';
import { UserHelper } from '@/helpers/user.helper';
import { Paging } from '@/models/Paging';
import { AlertService } from '@/services/alert.service';
import { CurrencyPipe, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { BusinessManagementService, Payment, PaymentStatus, PaymentType } from '@eemo/api-client';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, catchError, map, merge, of as observableOf, startWith, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SelectComponent } from '../../../components/ui-forms/select/select.component';

import { InputComponent } from '@/components/ui-forms/input/input.component';

@Component({
  selector: 'app-admin-payments',
  templateUrl: './admin-payments.component.html',
  styleUrls: ['./admin-payments.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    InputComponent,
    ReactiveFormsModule,
    MatExpansionModule,
    SelectComponent,
    MatSlideToggleModule,
    MatDividerModule,
    NgIf,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatPaginatorModule,
    CurrencyPipe,
    DateTimeFormatPipe
  ]
})
export class AdminPaymentsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource: MatTableDataSource<Payment>;
  displayFullColumns = false;
  lightColumns = ['type', 'status', 'user', 'date', 'amountTaxIncl', 'actions'];
  fullColumns = [
    'type',
    'status',
    'user',
    'date',
    'amountTaxIncl',
    'amountTaxExcl',
    'amountVat',
    'externalId',
    'uid',
    'actions'
  ];
  displayedColumns = [];
  resultsLength = 0;
  isLoadingResults = true;

  startDateInvalid = false;
  endDateInvalid = false;
  filterStartDateInput: string;
  filterEndDateInput: string;
  search: string;

  availableTypes: PaymentType[];
  availableStatus: PaymentStatus[];

  sub = new Subscription();
  cancelSub = new Subscription();
  addTrialSub = new Subscription();
  addOfferedSub = new Subscription();

  paging = new Paging();

  get form_name() {
    return this.form.get('name') as FormControl<string>;
  }
  get form_type() {
    return this.form.get('type') as FormControl<PaymentType>;
  }
  get form_status() {
    return this.form.get('status') as FormControl<PaymentStatus>;
  }
  get form_dateStart() {
    return this.form.get('dateStart') as FormControl<string>;
  }
  get form_dateEnd() {
    return this.form.get('dateEnd') as FormControl<string>;
  }

  form = this.fb.group({
    name: [null],
    type: [null],
    status: [null],
    dateStart: [null],
    dateEnd: [null]
  });

  constructor(
    private fb: UntypedFormBuilder,
    private businessService: BusinessManagementService,
    private alerteService: AlertService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private changeDetectorRefs: ChangeDetectorRef,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.displayedColumns = this.lightColumns;
  }

  ngOnInit() {
    this.availableTypes = Object.keys(PaymentType) as PaymentType[];
    this.availableStatus = Object.keys(PaymentStatus) as PaymentStatus[];
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.loadData();
  }

  loadData() {
    this.sub = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          return this.businessService.getPaymentList(
            this.search,
            [this.form_type.value],
            [this.form_status.value],
            this.form_dateStart.value ? (new Date(this.form_dateStart.value).toISOString() as unknown as Date) : null,
            this.form_dateEnd.value ? (new Date(this.form_dateEnd.value).toISOString() as unknown as Date) : null,
            this.paginator.pageIndex * this.paginator.pageSize,
            this.paginator.pageSize
          );
        }),
        map((dataList) => {
          // Flip flag to show that loading has finished.
          this.isLoadingResults = false;
          this.resultsLength = dataList.paging.totalItems;
          this.alerteService.clear();
          return dataList.list;
        }),
        catchError((error) => {
          this.isLoadingResults = false;
          return observableOf([]);
        })
      )
      .subscribe((data) => {
        const displayedData = [];
        data.forEach((i: Payment) => {
          const row = {
            payment: i,
            uid: i.uid,
            type: this.translate.instant('enum.PaymentType.' + i.type),
            status: this.translate.instant('enum.PaymentStatus.' + i.status),
            statusClass: i.status.toLowerCase(),
            date: i.date,
            amountTaxIncl: i.amountTaxIncl,
            amountTaxExcl: i.amountTaxExcl,
            amountVat: i.amountVat,
            user: i.user ? (i.user.name ? UserHelper.userSummmaryDisplayName(i.user) : i.user.uid) : '',
            externalId: i.externalId,
            stripeUrl: environment.stripeUrl + '/payments/' + i.externalId
          };
          displayedData.push(row);
        });
        this.dataSource = new MatTableDataSource(displayedData);
      });
  }

  updateDataFromFilters() {
    const filterValue = this.form_name.value;
    if (!filterValue || filterValue.length > 1) {
      this.search = filterValue ? filterValue.trim().toLowerCase() : null;
      this.paginator.pageIndex = 0;
      this.loadData();
    }
  }

  toggleDisplayColumns() {
    if (this.displayFullColumns) {
      this.displayFullColumns = false;
      this.displayedColumns = this.lightColumns;
    } else {
      this.displayFullColumns = true;
      this.displayedColumns = this.fullColumns;
    }
    this.changeDetectorRefs.detectChanges();
  }
}
